/**
 * 忘记密码
 */
import React, { useState, useEffect, useRef } from 'react'
import { FormPro, Input, Button, Icon } from '@roo/roo'
import { useTranslation } from '@sailor/i18n-web'
import { successToast, failToast } from '@/common/utils/toast'
import styles from './index.module.less'
import { useRequest } from 'ahooks'
import EmailSvg from '@/components/Icons/EmailSvg'
import ResetPswForm from './components/ForgetPswForm'
import PageBox from '@/components/PageBox'
import { sendVerificationCode, verifyCodeEmail } from './api'
import { jumpYodaVerify } from '@/common/utils'
import { useNavigate } from 'react-router'


const ForgotPassword = () => {
  const { t } = useTranslation()
  const [verifyInfo, setVerifyInfo] = useState<undefined | { email: string, code: string }>({ email: '', code: '' })
  const [countdown, setCountdown] = useState(0)
  const [emailForm] = FormPro.useForm()
  const countingDownTimer = useRef()
  const navigate = useNavigate()

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown(prev => prev - 1)
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [countdown])

  function startCountingDown () {
    setCountdown(60)
    countingDownTimer.current = setInterval(() => {
      setCountdown(pre => {
        const next = pre - 1
        if (next <= 0) {
          clearInterval(countingDownTimer.current)
        }
        return next
      })
    }, 1000)
  }

  // 发送验证码
  const { loading: sendCodeLoading, run: sendCode } = useRequest(async () => {
    await emailForm.validateFields(['email']) // 发送前,需要先校验邮箱是否正常
    const email = emailForm.getFieldValue('email')?.trim()
    await sendVerificationCode({ userName: email })
    successToast(t('e_c_msg_code_sent', '验证码已发送到您的邮箱'))
    startCountingDown()
  }, {
    manual: true,
    onError: (error) => {
      if (error?.data?.requestCode) {
        jumpYodaVerify(error?.data?.requestCode)
      }
    // failToast(t('e_c_msg_code_sent', '验证码已发送到您的邮箱'))
    } })

  // 验证码
  const { loading: verifyCodeLoading, run: verifyCode } = useRequest(async () => {
    const values = await emailForm.getFieldsValue()
    await verifyCodeEmail({ userName: values.email?.trim(), verificationCode: values.code?.trim() })
    setVerifyInfo({ email: values.email, code: values.code })
  }, { manual: true })


  const onFormCheckFail = (errorInfo: any) => {
    try {
      const errorMsg = errorInfo.errorFields[0].errors[0]
      failToast(errorMsg)
    } catch (error) {
      console.log('Failed:', errorInfo)
    }
  }


  return (
    <PageBox>
      <div className={styles.container}>
        {!verifyInfo?.email
          ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', width: 'fit-content', cursor: 'pointer' }} onClick={() => {
                // 使用 react-router 返回上一页
                navigate(-1)
              }}>
                <Icon name="left" style={{ fontSize: '18px' }}/>&ensp;
                <span style={{ fontSize: '24px', fontWeight: '700', color: '#1f1f1f' }}> {t('e_pc_verify_email', '验证邮箱')} </span>
              </div>
              <FormPro
                name="emailForm"
                onFinish={verifyCode}
                onFinishFailed={onFormCheckFail}
                labelCol={{ span: 12 }}
                wrapperCol={{ span: 12 }}
                layout="vertical"
                form={emailForm}
              >
                <FormPro.Item
                  name="email"
                  label={t('e_pc_email_address', '邮箱地址')}
                  validateTrigger={['onBlur']}
                  rules={[
                    { required: true, message: t('e_c_input_email_label', '请输入邮箱地址') },
                    { type: 'email', message: t('e_pc_input_vaild_password', '请输入有效的邮箱地址') },
                  ]}
                >
                  <Input prefix={<div style={{ marginBottom: '-6px' }}><EmailSvg /></div>} placeholder={t('e_c_input_email_label', '请输入邮箱地址')} />
                </FormPro.Item>
                <FormPro.Item
                  name="code"
                  label={t('e_pc_verification_code', '验证码')}
                  validateTrigger={['onBlur']}
                  help={countdown > 0 &&  <span style={{ color: '#9E9E9E', fontSize: '12px' }}>{t('e_c_tip_check_spam', '若未找到验证码可查看垃圾邮件')}</span>}
                  rules={[
                    { required: true, message: t('e_c_input_code_placeholder', '请输入验证码') },
                  ]}
                >
                  <Input  placeholder={t('e_c_input_code_placeholder', '请输入验证码')}
                    suffix={
                      <Button type="text" loading={sendCodeLoading} onClick={sendCode} disabled={countdown > 0}>
                        <span style={{ color: '#9E9E9E' }}>
                          {
                            countdown > 0
                              ? `${t('e_c_btn_get_code_again', '再次获取')}(${countdown}s)`
                              : t('e_c_btn_get_verification', '发送验证码')
                          }
                        </span>
                      </Button>
                    }
                  />
                </FormPro.Item>
                <FormPro.Item>
                  <Button type="brand" htmlType="submit" style={{ width: '100%' }} loading={verifyCodeLoading} disabled={verifyCodeLoading}>
                    {t('e_pc_verify', '验证')}
                  </Button>
                </FormPro.Item>
              </FormPro>
            </div>
          )
          : (
            <div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', width: 'fit-content', cursor: 'pointer' }} onClick={() => {setVerifyInfo(undefined)}}>
                  <Icon name="left" style={{ fontSize: '18px' }}/>&ensp;
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#1f1f1f' }}> {t('e_pc_reset_password', '重置密码')} </span>
                </div>
                <div style={{ color: '#6b7280', padding: '15px 0' }}>
                  {t('e_pc_set_new_password_for_email', { email: verifyInfo?.email }) || `为邮箱${verifyInfo?.email}设置新密码`}
                </div>
              </div>
              <ResetPswForm verifyInfo={verifyInfo}/>
            </div>
          )}
      </div>
    </PageBox>
  )
}

export default ForgotPassword